/**
 * Task sul computer, ridisegnati il 26/10/2026 (os-planning/ridisegno/CAPITOLATO.md).
 *
 * La domanda: cosa c'è da fare, e chi lo fa?
 * La forma: una lista con le spunte a gruppi per quando scade (in ritardo, oggi,
 * nei prossimi 7 giorni, più avanti, senza scadenza), l'aggiunta veloce in cima
 * e in testa la risposta in una frase. La bacheca a due colonne resta a un clic.
 *
 * Alleggerita (Lorenzo, 26/10: "troppe cose inutili"): via i quattro numeri in
 * testa (li dice la frase), le linguette Aperti/Completati (i completati sono
 * una scelta della striscia "di chi"), la tendina dei clienti e "Ordina/Filtra"
 * (l'ordine lo dà il gruppo). I filtri vecchi nell'indirizzo (due, prio,
 * assignee, client) valgono ancora: se ce n'è uno acceso, una riga lo dice e
 * lo toglie. Sul telefono resta TasksMobile, con la stessa forma.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { Icon } from '../../components/ui/Icon';
import { Avatar } from '../../components/ui/Avatar';
import { BrandLogo } from '../../components/ui/BrandLogo';
import { SectionMark } from '../../components/ui/SectionMark';
import { EmptyState } from '../../components/ui/EmptyState';
import { useUrlState } from '../../hooks/useUrlState';
import { leggibile } from '../../components/mobile/MobileKit';
import { ModuleKey, PermissionLevel, hasAtLeast } from '@masamune/shared';
import { fmtDate } from '../../lib/utils';
import {
  tasksSummaryQuery, tasksListQuery, assignableUsersQuery, SCOPES, dueInfo, fullName, isDone, isHigh,
  type TaskItem, type TaskScope, type TaskDueFilter,
} from './tasksApi';
import { useTaskActions } from './useTaskActions';
import { CalendarFeedPopover } from './CalendarFeed';
import { KanbanView, QuickAdd } from './TasksPage';
import './tasks-desktop.css';

const VIEW_KEY = 'tasks-view';
type Vista = TaskScope | 'done';
const VISTE: Array<{ k: Vista; label: string }> = [
  { k: 'mine', label: 'I miei' },
  { k: 'assigned-by-me', label: 'Dati da me' },
  { k: 'all', label: 'Tutti' },
  { k: 'done', label: 'Completati' },
];

const DUE_LABEL: Record<string, string> = {
  overdue: 'solo quelli in ritardo', today: 'solo quelli che scadono oggi', week: 'solo quelli dei prossimi 7 giorni', none: 'solo quelli senza scadenza',
};

/** I gruppi della lista, nell'ordine in cui servono. */
function gruppoDi(t: TaskItem): { chiave: string; titolo: string; ordine: number } {
  const g = dueInfo(t.dueDate).days;
  if (g == null) return { chiave: 'senza', titolo: 'Senza scadenza', ordine: 4 };
  if (g < 0) return { chiave: 'ritardo', titolo: 'In ritardo', ordine: 0 };
  if (g === 0) return { chiave: 'oggi', titolo: 'Oggi', ordine: 1 };
  if (g <= 7) return { chiave: 'settimana', titolo: 'Nei prossimi 7 giorni', ordine: 2 };
  return { chiave: 'dopo', titolo: 'Più avanti', ordine: 3 };
}

/** Quando, in parole, dentro il suo gruppo: il gruppo dice già "in ritardo" o "oggi". */
function quando(t: TaskItem, gruppo: string): { testo: string; alert?: boolean } {
  if (!t.dueDate) return { testo: '' };
  const g = dueInfo(t.dueDate).days ?? 0;
  const d = new Date(t.dueDate);
  if (gruppo === 'ritardo') return { testo: g === -1 ? 'da ieri' : `da ${-g} giorni`, alert: true };
  if (gruppo === 'oggi') return { testo: 'oggi' };
  if (g === 1) return { testo: 'domani' };
  if (gruppo === 'settimana') return { testo: d.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric' }) };
  return { testo: d.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', ...(d.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}) }) };
}

export function TasksDesktop() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const canWrite = useMemo(() => {
    if (!user) return false;
    const lvl = user.permissions.find((p) => p.module === ModuleKey.TASKS)?.level ?? PermissionLevel.NONE;
    return hasAtLeast(lvl, PermissionLevel.WRITE);
  }, [user]);

  // Stessi parametri nell'indirizzo di prima: tab, scope, q (e i filtri nascosti).
  const [tabParam] = useUrlState<string>('tab', 'open');
  const [scopeParam] = useUrlState<string>('scope', 'mine');
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const scope: TaskScope = (SCOPES as string[]).includes(scopeParam) ? (scopeParam as TaskScope) : 'mine';
  const fatti = tabParam === 'done';
  const vista: Vista = fatti ? 'done' : scope;

  const dueParam = searchParams.get('due') ?? '';
  const due: TaskDueFilter = (['today', 'overdue', 'week', 'none'] as string[]).includes(dueParam) ? (dueParam as TaskDueFilter) : '';
  const prio = searchParams.get('prio') === 'ALTA' || searchParams.get('prio') === 'NORMALE' ? (searchParams.get('prio') as 'ALTA' | 'NORMALE') : undefined;
  const assignee = searchParams.get('assignee') ?? '';
  const client = searchParams.get('client') ?? '';
  const nascosti = [due && DUE_LABEL[due], prio && (prio === 'ALTA' ? 'solo la priorità alta' : 'solo la priorità normale'), assignee && 'di una persona sola', client && 'di un cliente solo'].filter(Boolean) as string[];

  const [view, setViewState] = useState<'list' | 'kanban'>(() => {
    try { return localStorage.getItem(VIEW_KEY) === 'kanban' ? 'kanban' : 'list'; } catch { return 'list'; }
  });
  const setView = (v: 'list' | 'kanban') => { setViewState(v); try { localStorage.setItem(VIEW_KEY, v); } catch { /* niente */ } };

  const scegli = (v: Vista) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      if (v === 'done') sp.set('tab', 'done');
      else { sp.delete('tab'); if (v === 'mine') sp.delete('scope'); else sp.set('scope', v); }
      return sp;
    }, { replace: true });
  };
  const togliFiltri = () => setSearchParams((prev) => {
    const sp = new URLSearchParams(prev);
    ['due', 'prio', 'assignee', 'client'].forEach((k) => sp.delete(k));
    return sp;
  });

  const { data: summary } = useQuery(tasksSummaryQuery(scope));
  const { data: users } = useQuery(assignableUsersQuery);
  const base = {
    scope,
    search: search.trim() || undefined,
    due: due || undefined,
    priorita: prio,
    assigneeId: assignee || undefined,
    clientId: client || undefined,
  };
  const listParams = { ...base, status: fatti ? 'DONE' : undefined, includeDone: fatti, sort: fatti ? 'completati' : 'scadenza', page: 1, pageSize: 200 };
  const kanbanParams = { ...base, includeDone: true, page: 1, pageSize: 200 };
  const listQ = useQuery({ ...tasksListQuery(listParams), enabled: view === 'list' });
  const kanbanQ = useQuery({ ...tasksListQuery(kanbanParams), enabled: view === 'kanban' });
  const items = listQ.data?.items ?? [];

  const gruppi = useMemo(() => {
    if (fatti) return [{ chiave: 'fatti', titolo: 'Completati, i più recenti per primi', ordine: 0, items }];
    const m = new Map<string, { chiave: string; titolo: string; ordine: number; items: TaskItem[] }>();
    for (const t of items) {
      const g = gruppoDi(t);
      if (!m.has(g.chiave)) m.set(g.chiave, { ...g, items: [] });
      m.get(g.chiave)!.items.push(t);
    }
    return [...m.values()].sort((a, b) => a.ordine - b.ordine);
  }, [items, fatti]);

  // La risposta in una frase, coi numeri dentro.
  const todo = summary?.todo ?? 0;
  const rit = summary?.overdue ?? 0;
  const oggi = summary?.dueToday ?? 0;
  const coda = [rit > 0 && <span key="r" className="tk2-alert">{rit} in ritardo</span>, oggi > 0 && <span key="o">{oggi} per oggi</span>].filter(Boolean) as JSX.Element[];
  const codaFrase = coda.length === 2 ? <>: {coda[0]} e {coda[1]}</> : coda.length === 1 ? <>: {coda[0]}</> : null;
  const risposta = !summary ? 'Conto i task…'
    : fatti ? <>Questa settimana {summary.doneThisWeek === 1 ? 'è stato completato' : 'sono stati completati'} <strong>{summary.doneThisWeek} {summary.doneThisWeek === 1 ? 'task' : 'task'}</strong>.</>
    : todo === 0 ? (scope === 'assigned-by-me' ? 'Nessun task dato agli altri è ancora da fare.' : 'Niente da fare: la lista è vuota.')
    : scope === 'mine' ? <>Hai <strong>{todo} task da fare</strong>{codaFrase}.</>
    : scope === 'assigned-by-me' ? <>Hai dato agli altri <strong>{todo} task</strong> ancora da fare{codaFrase}.</>
    : <>In tutto ci sono <strong>{todo} task da fare</strong>{codaFrase}.</>;

  return (
    <div className="page tk2">
      <header className="tk2-hd">
        <div className="tk2-hd-main">
          <SectionMark icon="tasks" className="tk2-sez" />
          <div className="min-w-0">
            <h1 className="tk2-title">Task</h1>
            <p className="tk2-risposta">{risposta}</p>
          </div>
        </div>
      </header>

      <div className="tk2-bar">
        <div className="tk2-seg" role="tablist" aria-label="Quali task">
          {VISTE.map((v) => (
            <button key={v.k} type="button" role="tab" aria-selected={vista === v.k} className="tk2-seg-btn" onClick={() => scegli(v.k)}>{v.label}</button>
          ))}
        </div>
        <label className="tk2-search">
          <Icon name="search" size={13} />
          <input aria-label="Cerca fra i task" placeholder="Cerca per titolo, cliente o progetto" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
        <div className="spacer" />
        <div className="tk2-viste" role="group" aria-label="Come vedere i task">
          <button type="button" aria-pressed={view === 'list'} title="Lista" aria-label="Lista" onClick={() => setView('list')}><Icon name="list" size={15} /></button>
          <button type="button" aria-pressed={view === 'kanban'} title="Bacheca a due colonne" aria-label="Bacheca" onClick={() => setView('kanban')}><Icon name="kanban" size={15} /></button>
        </div>
        <CalendarFeedPopover iconOnly />
      </div>

      {nascosti.length > 0 && (
        <div className="tk2-filtro">
          <Icon name="filter" size={12} /> Stai vedendo {nascosti.join(', ')}.
          <button type="button" onClick={togliFiltri}>Mostra tutti</button>
        </div>
      )}

      {view === 'kanban' ? (
        <KanbanView
          queryKey={tasksListQuery(kanbanParams).queryKey}
          isLoading={kanbanQ.isLoading && !kanbanQ.data}
          items={kanbanQ.data?.items ?? []}
          canWrite={canWrite}
          meId={user?.id ?? ''}
          hasFilters={nascosti.length > 0 || !!search.trim()}
          onClearFilters={() => { togliFiltri(); setSearch(''); }}
          onOpen={(id) => navigate(`/tasks/${id}`)}
        />
      ) : (
        <>
          {canWrite && !fatti && <QuickAdd users={users ?? []} meId={user?.id ?? ''} />}
          {listQ.isLoading && !listQ.data ? (
            <div className="tk2-gruppo"><div className="skeleton-col"><span className="skeleton sk-block" /><span className="skeleton sk-block" /><span className="skeleton sk-block" /></div></div>
          ) : items.length === 0 ? (
            <EmptyState
              brand
              title={search.trim() ? `Nessun task con "${search.trim()}"` : fatti ? 'Nessun task completato' : 'Niente da fare'}
              description={search.trim() ? 'Prova con un’altra parola.' : fatti ? 'Quando ne chiudi uno, finisce qui.' : canWrite ? 'Scrivi qui sopra il primo task e premi Invio.' : 'La lista è vuota.'}
            />
          ) : (
            gruppi.map((g) => (
              <section key={g.chiave} className={`tk2-gruppo${g.chiave === 'ritardo' ? ' is-ritardo' : ''}`}>
                <h2 className="tk2-gruppo-t">{g.titolo}<span className="tk2-gruppo-n">{g.items.length}</span></h2>
                <ul className="tk2-lista">
                  {g.items.map((t) => <Riga key={t.id} t={t} gruppo={g.chiave} canWrite={canWrite} onOpen={() => navigate(`/tasks/${t.id}`)} />)}
                </ul>
              </section>
            ))
          )}
        </>
      )}
    </div>
  );
}

/** Una riga: il cerchio lo completa (con la notifica e l'Annulla di sempre), il resto apre il task. */
function Riga({ t, gruppo, canWrite, onOpen }: { t: TaskItem; gruppo: string; canWrite: boolean; onOpen: () => void }) {
  const { complete, reopen } = useTaskActions();
  const done = isDone(t.status);
  const busy = complete.isPending || reopen.isPending;
  const q = done ? { testo: t.completedAt ? `fatto il ${fmtDate(t.completedAt)}` : 'fatto' } : quando(t, gruppo);
  const cliente = t.client ? leggibile(t.client.shortName) : null;
  const toggle = () => (done
    ? reopen.mutate({ id: t.id, title: t.title })
    : complete.mutate({ id: t.id, title: t.title, prevStatus: t.status, dueDate: t.dueDate, recurrence: t.recurrence }));

  return (
    <li className={`tk2-riga${done ? ' is-done' : ''}`}>
      <button
        type="button"
        className="tk2-check"
        aria-pressed={done}
        disabled={!canWrite || busy}
        aria-label={done ? `Riapri: ${t.title}` : `Segna come fatto: ${t.title}`}
        title={done ? 'Riapri' : 'Segna come fatto'}
        onClick={toggle}
      >
        <span className="tk2-ring">{done && <Icon name="check" size={12} strokeWidth={2.5} />}</span>
      </button>
      <button type="button" className="tk2-corpo" onClick={onOpen}>
        <span className="tk2-tit">
          <span className="tk2-tit-tx">{t.title}</span>
          {isHigh(t.priority) && !done && <span className="tk2-alta" title="Priorità alta">Alta</span>}
          {t.recurrence && t.recurrence !== 'NONE' && <Icon name="repeat" size={12} className="tk2-rip" />}
        </span>
        {t.project && <span className="tk2-sub">{t.project.code} · {t.project.name}</span>}
      </button>
      <span className="tk2-cliente">
        {cliente && <><BrandLogo name={cliente} size={20} /><span className="t">{cliente}</span></>}
      </span>
      <span className="tk2-chi" title={t.assignee ? fullName(t.assignee) : 'Non assegnato'}>
        {t.assignee
          ? <><Avatar initials={t.assignee.initials} size={22} name={fullName(t.assignee)} /><span className="t">{t.assignee.firstName}</span></>
          : <span className="tk2-mute">non assegnato</span>}
      </span>
      <span className={`tk2-quando${q.alert ? ' tk2-alert' : ''}`}>{q.testo}</span>
    </li>
  );
}
